import React from 'react';

import { ProductWizardStep as OneMenuProductWizardStep } from 'features/menu/components/product-wizard/types';
import { LayoutChangeEvent, ScrollView, View } from 'react-native';

import { IComboSlotOption } from '@rbi-ctg/menu';
import { useMaybePlayAudioOnceOnMount } from 'components/app-takeover/play-audio-once';
import { useAppTakeoverPdp } from 'components/app-takeover/use-app-takeover';
import { InnerLayout, LayoutContainer } from 'components/layout/styled';
import { PageAnnouncer } from 'components/page-announcer';
import { ProductCta } from 'components/product-detail-v2/product-cta';
import { ProductDetailHeader } from 'components/product-detail-v2/product-detail-flow/product-detail-header';
import {
  transformComboSlotTypeToEventAttr,
  transformProductWizardStepToEventAttr,
} from 'components/product-detail-v2/utils';
import { MenuObjectTypes } from 'enums/menu';
import { useModifierRedesignPhaseTwo } from 'experiments/use-modifier-redesign-phase-two';
import { useIsMobileBreakpoint } from 'hooks/use-media-query';
import { t } from 'state/intl/translations';
import { useProductWizardContext } from 'state/product-wizard-v2';
import useProductWizardFlow from 'state/product-wizard-v2/hooks/use-product-wizard-flow';
import { ProductWizardStep } from 'state/product-wizard-v2/types';
import { isPicker } from 'utils/menu';

import { ProductCustomizationModifiers } from '../product-customization-modifiers';
import { ProductCustomizationOptions } from '../product-customization-options';

import { IProductCustomizationItem } from './types';

const MODIFIERS_VIEW_MIN_HEIGHT = 240;

export const ProductCustomizationItem: React.FC<IProductCustomizationItem> = ({
  slotKeyToCustomize,
  comboSlot,
  comboSlotOptions,
  currentStep,
  selectedItem,
  handleComboSlotSelectionChange,
  modifierSelections,
  defaultModifiersMap,
  handleChangeModifierSelections,
  handleResetModifierSelections,
  pickerAspectExclusions,
  onPressBackNavigation,
  backNavigationLabel = '',
}) => {
  const isMobile = useIsMobileBreakpoint();
  const scrollViewRef = React.useRef<ScrollView>(null);
  const modifiersRef = React.useRef<View>(null);
  const modifiersPosition = React.useRef<number>(0);

  const { menuObject, updateProductWizardFlowSteps, comboSlotUpdate } = useProductWizardContext();
  const { isProductReviewFromCart } = useProductWizardFlow();
  const hasAvailableComboSlotOptions = comboSlotOptions.length > 1;
  const showExpandedOptions = hasAvailableComboSlotOptions;

  const foundPickerFlowComboSlotUpdate = comboSlotUpdate.find(comboSlotUpdateVal => {
    return slotKeyToCustomize === comboSlotUpdateVal.id;
  });

  const { additionalAudioSteps } = useAppTakeoverPdp(menuObject?._id);
  useMaybePlayAudioOnceOnMount(
    additionalAudioSteps?.[OneMenuProductWizardStep.PickerCustomization]
  );

  const showModifiers =
    !!foundPickerFlowComboSlotUpdate?.isDirty ||
    comboSlotOptions.length <= 1 ||
    !isPicker(menuObject) ||
    isProductReviewFromCart;

  const setModifiersPosition = (event: LayoutChangeEvent) => {
    modifiersPosition.current = event.nativeEvent.layout.y;
  };

  const handleSlotChange = React.useCallback(
    (option: IComboSlotOption) => {
      handleComboSlotSelectionChange(option);

      const comboSlotHasAvailableOptions = comboSlotOptions.length > 1;
      const comboSlotOptionHasModifiers = !!option?.option.options.length;
      // And scroll to ProductCustomizationModifiers position.
      const canScrollToModifiers =
        scrollViewRef.current &&
        modifiersPosition.current &&
        comboSlotHasAvailableOptions &&
        comboSlotOptionHasModifiers;

      if (canScrollToModifiers) {
        scrollViewRef?.current?.scrollTo({
          y: modifiersPosition.current,
          animated: true,
        });
      }
    },
    [comboSlotOptions.length, handleComboSlotSelectionChange]
  );

  React.useEffect(() => {
    updateProductWizardFlowSteps({
      id: slotKeyToCustomize,
      type: MenuObjectTypes.ITEM,
      currentStep: true,
    });
  }, [slotKeyToCustomize, updateProductWizardFlowSteps]);

  // Log Control experiment exposure if necessary
  const hasMultipleComboSlotOptions = comboSlotOptions.length > 1;
  const skipExposureLogging = !(hasMultipleComboSlotOptions && showModifiers);
  useModifierRedesignPhaseTwo(skipExposureLogging);

  if (!selectedItem) {
    return null;
  }

  const shouldDisplaySelectedItemNameTitle =
    !isPicker(menuObject) || currentStep === ProductWizardStep.ProductEditOrder;

  const productDetailHeaderTitle =
    shouldDisplaySelectedItemNameTitle && selectedItem.name.locale
      ? selectedItem.name.locale
      : t('haveItYourWay');

  return (
    <>
      <ProductDetailHeader
        title={productDetailHeaderTitle}
        currentStep={ProductWizardStep.ProductCustomization}
        onPress={onPressBackNavigation}
        backNavigationLabel={backNavigationLabel}
        eventAttributes={{
          name: transformProductWizardStepToEventAttr(currentStep),
          comboSlotType: transformComboSlotTypeToEventAttr('item'),
        }}
      />

      <ScrollView ref={scrollViewRef} showsVerticalScrollIndicator>
        <LayoutContainer isFullContainer={isMobile}>
          <InnerLayout>
            {showExpandedOptions && (
              <ProductCustomizationOptions
                slotKeyToCustomize={slotKeyToCustomize}
                comboSlot={comboSlot}
                comboSlotOptions={comboSlotOptions}
                selectedItem={selectedItem}
                onSelectComboSlot={handleSlotChange}
              />
            )}
            <View
              ref={modifiersRef}
              onLayout={setModifiersPosition}
              style={{ minHeight: MODIFIERS_VIEW_MIN_HEIGHT }}
            >
              {showModifiers && (
                <ProductCustomizationModifiers
                  modifierSelections={modifierSelections}
                  modifierDefaults={defaultModifiersMap}
                  onModifierSelectionsChange={handleChangeModifierSelections}
                  onResetModifierSelections={handleResetModifierSelections}
                  pickerAspectExclusions={pickerAspectExclusions}
                  selectedItem={selectedItem}
                />
              )}
            </View>
          </InnerLayout>
        </LayoutContainer>
      </ScrollView>

      <ProductCta
        currentStep={currentStep}
        menuObject={menuObject}
        modifierSelections={modifierSelections}
        selectedItem={selectedItem}
      />

      <PageAnnouncer pageTitle={productDetailHeaderTitle} />
    </>
  );
};
